/**
 * Core trip API calls: CRUD, list/summary variants, copy & merge,
 * booking statuses, visibility, completion, and trash/restore.
 */
import { API_BASE } from '../../config/api';
import { invalidateDashboardCache } from '../../hooks/useHomeDashboard';
import AuthService from '../authService';
import { apiRequest, failResult } from './request';
import { transformTripData } from './transform';

/**
 * Create a new trip with activities
 * @param {object} tripData - Trip details and activities
 * @returns {object} Result with created trip
 */
export async function createTrip(tripData) {
  const {
    name,
    description,
    startDate,
    endDate,
    location,
    tripType = 'leisure',
    status = 'planning',
    visibility = 'private',
    allowComments = true,
    allowLikes = true,
    budgetExpected,
    // Which itinerary view the trip opens in. The server defaults to
    // 'high-level' (right for a week); a day out passes 'detailed' because an
    // evening is inherently clock-ordered (DAY_OUT_SPEC §5).
    itineraryViewPreference,
    activities = [],
    travelers = [],
    // Structured destinations. The server's createTrip has always handled
    // these (→ saveTripDestinations, which resolves country codes, writes
    // all_destinations_text, and propagates the country signal up to the trip
    // row); this client just wasn't forwarding them. Shape per destination:
    // { name, type, countryCode, coordinates: {lat,lng}, isPrimary }.
    destinations = []
  } = tripData;

  // Create the trip with activities in a single request
  // Server's createTrip handles tripData.activities
  const tripResult = await apiRequest('/api/trips', {
    method: 'POST',
    body: {
      name,
      description,
      startDate,
      endDate,
      location,
      tripType,
      status,
      visibility,
      allowComments,
      allowLikes,
      budget_expected: budgetExpected,
      itineraryViewPreference,
      activities,
      travelers,
      destinations
    },
    errorLabel: 'creating trip'
  });

  if (!tripResult.success) {
    return tripResult;
  }

  // Get the complete trip with activities. The POST is the commit: if this
  // read fails, the POST's own trip still stands, or the caller reports a
  // failed create and a retry makes a second trip (BH-03-03).
  const completeTrip = await getTripById(tripResult.trip.id);

  return {
    success: true,
    trip: completeTrip || transformTripData(tripResult.trip),
    // Children the server would not invite (their parents' grant is gone),
    // so the caller can say who was left out (BH-03-04).
    refusedChildren: tripResult.refusedChildren || [],
    message: 'Trip created successfully!'
  };
}

/**
 * Map of activityId -> booking status ('confirmed'|'not_needed'|'unconfirmed') for the current user;
 * null when the request failed — not "nothing booked", so callers don't cache it.
 */
export function getBookingStatuses(tripId) {
  return apiRequest(`/api/trips/${tripId}/booking-status`, {
    errorLabel: 'getting booking statuses',
    transform: (data) => (data?.success ? (data.statuses || {}) : null),
    onError: () => null
  });
}

/** Set the current user's booking status for one trip activity. */
export function setBookingStatus(tripId, activityId, status) {
  return apiRequest(`/api/trips/${tripId}/activities/${activityId}/booking-status`, {
    method: 'PUT',
    body: { status },
    errorLabel: 'setting booking status',
    transform: (data) => (data?.success ? data : { success: false }),
    onError: () => ({ success: false })
  });
}

/**
 * Get a single trip by ID
 * @param {string} tripId - Trip ID
 * @returns {object|null} Trip with activities
 */
export function getTripById(tripId) {
  return apiRequest(`/api/trips/${tripId}`, {
    errorLabel: 'getting trip',
    transform: (data) => {
      if (!data.success) {
        return null;
      }
      // Use activities from the trip object (already returned by backend)
      // Backend getTripById() includes activities from trip_activities table
      const activities = data.trip.activities || [];
      return {
        ...transformTripData(data.trip),
        activities
      };
    },
    onError: () => null
  });
}

/**
 * Get a preview of what would be copied from a trip (day count, collections,
 * packing items). Used by the Copy Trip modal.
 */
export function getTripCopyPreview(tripId) {
  return apiRequest(`/api/trips/${tripId}/copy-preview`, {
    errorLabel: 'getting trip copy preview',
    transform: (data) => (data?.success ? data.preview : null),
    onError: () => null
  });
}

/**
 * What changes if this trip's visibility moves to `targetVisibility`.
 * Used by the pre-publish confirm in SharingModal: which stays' addresses
 * become publicly readable, how many followers get notified on a first
 * publish, and whether the trip becomes search-indexable.
 */
export function getTripPublishPreview(tripId, targetVisibility) {
  return apiRequest(
    `/api/trips/${tripId}/publish-preview?visibility=${encodeURIComponent(targetVisibility)}`,
    {
      errorLabel: 'getting trip publish preview',
      transform: (data) => (data?.success ? data.preview : null),
      onError: () => null
    }
  );
}

/**
 * Copy a trip — either into a brand new trip (target='new', default) or
 * merged into an existing trip the user can edit (target='existing').
 *
 * Common opts:
 *   - selectedDayNumbers?: number[]  (1-indexed source days; omit for all)
 *   - includeCollectionIds?: string[] | null  (null/omit = all)
 *   - includePackingList?: boolean
 *
 * For target='new':
 *   - newTripName?: string
 *   - startDate?: YYYY-MM-DD
 *
 * For target='existing':
 *   - targetTripId: string (required)
 *   - insertAtDayNumber: number (required, 1-indexed)
 *   - insertMode?: 'replace' (default) | 'shift'
 *
 * @returns {object} { success, trip, copyDetails } | { success: false, error }
 */
export function copyTrip(sourceTripId, opts = {}) {
  const target = opts.target === 'existing' ? 'existing' : 'new';
  const body = {
    target,
    includeCollectionIds: opts.includeCollectionIds === undefined ? null : opts.includeCollectionIds,
    selectedDayNumbers: Array.isArray(opts.selectedDayNumbers) ? opts.selectedDayNumbers : null
  };
  if (target === 'existing') {
    body.targetTripId = opts.targetTripId;
    body.insertAtDayNumber = opts.insertAtDayNumber;
    body.insertMode = opts.insertMode === 'shift' ? 'shift' : 'replace';
    body.includePackingList = opts.includePackingList === true;
  } else {
    body.newTripName = opts.newTripName;
    body.startDate = opts.startDate;
    body.includePackingList = opts.includePackingList !== false;
  }

  return apiRequest(`/api/trips/${sourceTripId}/copy`, {
    method: 'POST',
    body,
    errorLabel: 'copying trip',
    transform: (data) => {
      if (data?.success) {
        invalidateDashboardCache();
      }
      return data;
    }
  });
}

/**
 * Preview the impact of merging source days into THIS trip (the destination)
 * at a given position and mode. Returns affected day rows and the projected
 * new end date so the modal can warn before the user confirms.
 *
 * @param {string} destTripId - trip the user wants to merge INTO
 * @param {object} opts - { insertAtDayNumber, copyDayCount, insertMode }
 */
export function getInsertPreview(destTripId, opts = {}) {
  const params = new URLSearchParams({
    insertAtDayNumber: String(opts.insertAtDayNumber || ''),
    copyDayCount: String(opts.copyDayCount || ''),
    mode: opts.insertMode === 'shift' ? 'shift' : 'replace'
  });
  return apiRequest(`/api/trips/${destTripId}/insert-preview?${params.toString()}`, {
    errorLabel: 'getting insert preview',
    transform: (data) => (data?.success ? data.preview : null),
    onError: () => null
  });
}

/**
 * Get lightweight trip summary (count + scheduled entries for planner tabs)
 */
export function getUserTripsSummary() {
  return apiRequest('/api/trips/user/me/summary', {
    errorLabel: 'getting trip summary',
    transform: (data) => ({
      count: data.count || 0,
      scheduledTrips: (data.scheduledTrips || []).map(trip => transformTripData(trip))
    }),
    onError: () => ({ count: 0, scheduledTrips: [] })
  });
}

/**
 * Get lightweight trip list for card display (Trips tab).
 * Returns only fields needed for PlannerTripCard — no full activities/photos.
 * `upcoming: true` asks the server to skip past trips (dateless and
 * recently-ended trips still included) — used by Home's Coming Up section so
 * the payload doesn't grow with a lifetime of trip history.
 */
export function getUserTripsList({ throwOnError = false, upcoming = false } = {}) {
  return apiRequest(`/api/trips/user/me/list${upcoming ? '?upcoming=1' : ''}`, {
    errorLabel: 'getting trip list',
    transform: (data) => (data.trips || []).map(trip => {
      const transformed = transformTripData(trip);
      // Map lightweight fields to what PlannerTripCard expects
      transformed.activity_count = trip.activity_count || 0;
      transformed.travelers = trip.travelers || [];
      transformed.traveler_count = trip.traveler_count || transformed.travelers.length;
      transformed.myFeedbackCompleted = trip.myFeedbackCompleted || false;
      // Build tripCardPhotos from cover_photo if available
      if (trip.cover_photo) {
        transformed.tripCardPhotos = [{
          id: 'cover',
          url: trip.cover_photo,
          title: trip.name
        }];
      }
      return transformed;
    }),
    onError: (error) => {
      if (throwOnError) throw error;
      return [];
    }
  });
}

/**
 * Home's plans: the upcoming lean list plus the plan facts Home derives a
 * trip's stage from (day_count, planned_day_count, first_empty_day, stay,
 * destination, legs, updated_at) — src/utils/homePlans.js reads them.
 */
export function getHomePlans({ throwOnError = false } = {}) {
  return apiRequest('/api/trips/user/me/plans', {
    errorLabel: 'getting home plans',
    transform: (data) => (data.trips || []).map(trip => {
      const transformed = transformTripData(trip);
      transformed.activity_count = trip.activity_count || 0;
      transformed.travelers = trip.travelers || [];
      transformed.traveler_count = trip.traveler_count || transformed.travelers.length;
      transformed.myFeedbackCompleted = trip.myFeedbackCompleted || false;
      transformed.myReviewSkipped = trip.myReviewSkipped || false;
      if (trip.cover_photo) {
        transformed.tripCardPhotos = [{ id: 'cover', url: trip.cover_photo, title: trip.name }];
      }
      return transformed;
    }),
    onError: (error) => {
      if (throwOnError) throw error;
      return [];
    }
  });
}

/**
 * Bulk-fetch card photos for multiple trips in a single request.
 * @returns {Object<tripId, Array<photo>>}
 */
export function getTripCardPhotosBatch(tripIds) {
  if (!tripIds || tripIds.length === 0) return Promise.resolve({});
  const qs = encodeURIComponent(tripIds.join(','));
  return apiRequest(`/api/trips/card-photos/bulk?trip_ids=${qs}`, {
    errorLabel: 'getting bulk trip card photos',
    transform: (data) => data.photos || {},
    onError: () => ({})
  });
}

/**
 * Get current user's trips (full details — used for trip detail pages)
 * @returns {array} Array of trips
 */
export function getUserTrips({ throwOnError = false } = {}) {
  return apiRequest('/api/trips/user/me', {
    errorLabel: 'getting user trips',
    transform: (data) => (data.trips || []).map(trip => transformTripData(trip)),
    onError: (error) => {
      if (throwOnError) throw error;
      return [];
    }
  });
}

/**
 * Update an existing trip
 * @param {string} tripId - Trip ID
 * @param {object} updates - Fields to update
 * @returns {object} Result with updated trip
 */
export async function updateTrip(tripId, updates) {
  const result = await apiRequest(`/api/trips/${tripId}`, {
    method: 'PUT',
    body: updates,
    errorLabel: 'updating trip'
  });

  if (!result.success) {
    return result;
  }

  // Get the complete updated trip with activities
  const completeTrip = await getTripById(tripId);

  return {
    success: true,
    trip: completeTrip,
    message: 'Trip updated successfully!'
  };
}

/**
 * Fetch the "best for" tag taxonomy (dimensions + slugs + labels + select
 * rule). Static reference data driving the trip tag editor and discovery
 * filter. Public endpoint — no auth required.
 * @returns {object} { success, dimensions: [{ key, label, select, tags:[{slug,label}] }] }
 */
export async function getBestForTags() {
  try {
    const response = await fetch(`${API_BASE}/api/trips/best-for-tags`);
    return await response.json();
  } catch (error) {
    console.error('Error fetching best-for tags:', error);
    return failResult(error);
  }
}

/**
 * Set or clear a trip's hero album (P3). Pass albumId = null to clear.
 * The hero album's cover becomes the trip's cover; Featured tiles open into it.
 */
export function setHeroAlbum(tripId, albumId) {
  return apiRequest(`/api/trips/${tripId}/hero-album`, {
    method: 'PATCH',
    body: { album_id: albumId },
    errorLabel: 'setting hero album',
    transform: (data) => (data?.success ? data : { success: false, error: data?.error })
  });
}

/**
 * Fetch the full "Inspired by" source list for a trip (the "& N others"
 * modal). Privacy-gated server-side. Public endpoint (anonymous viewers see
 * public trips' provenance); auth header sent when present so friend-gated
 * sources resolve for logged-in viewers.
 * @returns {object} { success, provenance: { total, viewableCount, topSource, sources } }
 */
export async function getTripProvenance(tripId) {
  try {
    const response = await fetch(`${API_BASE}/api/trips/${tripId}/provenance`, {
      headers: AuthService.getJsonHeaders()
    });
    return await response.json();
  } catch (error) {
    console.error('Error fetching trip provenance:', error);
    return failResult(error);
  }
}

/**
 * Add an activity to a specific trip day
 * Used from search pages to save activities directly to the database
 * @param {string} tripId - Trip ID
 * @param {object} data - { activity, dayDate, scheduledTime, dayIndex, planVariant }
 * @returns {object} Result with activityId
 */
export function addActivityToTrip(tripId, data) {
  return apiRequest(`/api/trips/${tripId}/activities`, {
    method: 'POST',
    body: data,
    errorLabel: 'adding activity to trip'
  });
}

/**
 * Remove an activity from a trip
 * @param {string} tripId - Trip ID
 * @param {string} activityId - Activity ID to remove
 * @returns {object} Result
 */
export function removeActivityFromTrip(tripId, activityId) {
  return apiRequest(`/api/trips/${tripId}/activities/${activityId}`, {
    method: 'DELETE',
    errorLabel: 'removing activity from trip'
  });
}

/**
 * Delete a trip
 * @param {string} tripId - Trip ID
 * @returns {object} Result
 */
export function deleteTrip(tripId) {
  return apiRequest(`/api/trips/${tripId}`, {
    method: 'DELETE',
    errorLabel: 'deleting trip'
  });
}

/**
 * Get trip statistics
 * @param {string} tripId - Trip ID
 * @returns {object} Result with success flag and stats
 */
export function getTripStatistics(tripId) {
  return apiRequest(`/api/trips/${tripId}/stats`, {
    errorLabel: 'getting trip statistics',
    onError: () => ({ success: false, stats: null })
  });
}

/**
 * Update trip visibility and privacy settings
 * @param {string} tripId - Trip ID
 * @param {object} settings - { visibility?, allowComments?, allowLikes? }
 * @returns {object} Result with updated trip
 */
export async function updateTripVisibility(tripId, settings) {
  try {
    const updates = {};

    if (settings.visibility !== undefined) {
      updates.visibility = settings.visibility;
    }
    if (settings.allowComments !== undefined) {
      updates.allow_comments = settings.allowComments;
    }
    if (settings.allowLikes !== undefined) {
      updates.allow_likes = settings.allowLikes;
    }

    // Use the standard updateTrip method which handles visibility changes
    // and automatically sets shared_at when making public
    return await updateTrip(tripId, updates);
  } catch (error) {
    console.error('Error updating trip visibility:', error);
    return failResult(error);
  }
}

/**
 * Get user's deleted trips (trash)
 * @returns {array} Array of soft-deleted trips
 */
export function getDeletedTrips() {
  return apiRequest('/api/trips/deleted', {
    errorLabel: 'getting deleted trips',
    transform: (data) => (data.trips || []).map(trip => transformTripData(trip)),
    onError: () => []
  });
}

/**
 * Restore a deleted trip
 * @param {string} tripId - Trip ID to restore
 * @returns {object} Result with success status
 */
export function restoreTrip(tripId) {
  return apiRequest(`/api/trips/${tripId}/restore`, {
    method: 'POST',
    errorLabel: 'restoring trip'
  });
}

/**
 * "Skip" on a past trip's review (skip=true), or undo it (skip=false).
 * The viewer's own dismissal: the trip leaves their planner's review strip.
 * @returns {object} { success }
 */
export function setTripReviewSkip(tripId, skip) {
  return apiRequest(`/api/trips/${tripId}/review-skip`, {
    method: skip ? 'PUT' : 'DELETE',
    errorLabel: 'updating trip review'
  });
}

/**
 * Auto-complete trips whose end_date has passed
 * Marks trips with past end dates as 'completed'
 * @returns {object} Result with list of newly completed trips
 */
export function autoCompleteTrips() {
  return apiRequest('/api/trips/auto-complete', {
    method: 'POST',
    errorLabel: 'auto-completing trips'
  });
}
